import { throwOnError } from './utils';

export type UserTagGroup = {
  id: string;
  organization: {
    code: string;
    name: string;
  };
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  max_selections: number;
  is_active: boolean;
  // アクティブな subgroup を1件以上持つか（サーバ導出フラグ）。一覧・詳細の両方で返る。
  has_subgroups: boolean;
  created_at: string;
  updated_at: string;
};

// group 直下 item に埋め込まれる subgroup（中間層）表現。付与対象ではなく整理用の見出し。
export type EmbeddedUserTagSubgroup = {
  id: string;
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  is_active: boolean;
};

// subgroup CRUD / group 詳細の subgroups 配列で返る完全形。
export type UserTagSubgroup = EmbeddedUserTagSubgroup & {
  created_at: string;
  updated_at: string;
};

export type UserTagGroupItem = {
  id: string;
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  is_active: boolean;
  // 所属する subgroup。group 直下（未分類）の item は null。
  user_tag_subgroup: EmbeddedUserTagSubgroup | null;
  created_at: string;
  updated_at: string;
};

export type UserTagGroupDetail = UserTagGroup & {
  items: UserTagGroupItem[];
  // アクティブな subgroup のみ。作成順（created_at 昇順）。
  subgroups: UserTagSubgroup[];
};

type UserTagGroupsResponse = {
  rows: UserTagGroup[];
};

export const getUserTagGroups = (
  organizationCode: string,
  activeState: 'active' | 'inactive' | 'all' = 'active'
) =>
  fetch(
    `/api/user-tag-groups?organization_code=${encodeURIComponent(
      organizationCode
    )}&active_state=${activeState}`,
    {
      method: 'GET',
      credentials: 'same-origin',
    }
  ).then(throwOnError<UserTagGroupsResponse>);

export const getUserTagGroupDetail = (uuid: string) =>
  fetch(`/api/user-tag-groups/${uuid}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<UserTagGroupDetail>);

// 店舗登録/編集・申請画面で共通利用する正規化済みグループ。
// subgroup は選択対象ではなく、item 選択 UI の見出し（グルーピング）に使う。
export type NormalizedTagItem = {
  id: string;
  name: string;
  // 所属 subgroup。未分類（group 直下）の item は null。
  subgroupId: string | null;
  subgroupName: string | null;
};

export type NormalizedTagGroup = {
  id: string;
  name: string;
  maxSelections: number;
  items: NormalizedTagItem[];
  // アクティブな subgroup を作成順で保持。item 選択 UI の見出し順に使う。
  subgroups: { id: string; name: string }[];
};

// 発行体orgの有効グループ一覧＋各グループの有効itemを取得して正規化する。
// max_selections=0 は無制限。name は name_ja を優先（無ければ name_en）。
export const getActiveUserTagGroupsWithItems = async (
  organizationCode: string
): Promise<NormalizedTagGroup[]> => {
  // listing は active_state=active 既定で有効グループのみ返すが、念のためグループも is_active で絞る。
  const { rows } = await getUserTagGroups(organizationCode);
  const activeGroups = rows.filter((group) => group.is_active);
  const details = await Promise.all(activeGroups.map((group) => getUserTagGroupDetail(group.id)));
  return details.map((detail) => {
    // group 詳細の subgroups はアクティブのみ。削除済み subgroup を指す item は未分類扱いにするため
    // アクティブな subgroup id 集合で照合する。
    const activeSubgroups = (detail.subgroups || []).filter((sg) => sg.is_active);
    const activeSubgroupIds = new Set(activeSubgroups.map((sg) => sg.id));
    return {
      id: detail.id,
      name: detail.name_ja || detail.name_en,
      maxSelections: detail.max_selections,
      subgroups: activeSubgroups.map((sg) => ({ id: sg.id, name: sg.name_ja || sg.name_en })),
      items: (detail.items || [])
        .filter((item) => item.is_active)
        .map((item) => {
          const sg =
            item.user_tag_subgroup && activeSubgroupIds.has(item.user_tag_subgroup.id)
              ? item.user_tag_subgroup
              : null;
          return {
            id: item.id,
            name: item.name_ja || item.name_en,
            subgroupId: sg ? sg.id : null,
            subgroupName: sg ? sg.name_ja || sg.name_en : null,
          };
        }),
    };
  });
};

export const createUserTagGroup = (data: {
  organization_code: string;
  name_en: string;
  name_ja?: string;
  name_zh_hans?: string;
  name_zh_hant?: string;
  name_ko?: string;
  max_selections?: number;
}) =>
  fetch('/api/user-tag-groups', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagGroup>);

// max_selections は作成時のみ設定可。運用途中の変更を防ぐため更新では受け付けない。
export const updateUserTagGroup = (
  uuid: string,
  data: {
    name_en?: string;
    name_ja?: string;
    name_zh_hans?: string;
    name_zh_hant?: string;
    name_ko?: string;
  }
) =>
  fetch(`/api/user-tag-groups/${uuid}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagGroup>);

export const deleteUserTagGroup = (uuid: string) =>
  fetch(`/api/user-tag-groups/${uuid}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<UserTagGroup>);

export const createUserTagGroupItem = (
  groupUuid: string,
  data: {
    name_en?: string;
    name_ja?: string;
    name_zh_hans?: string;
    name_zh_hant?: string;
    name_ko?: string;
    // 所属させる subgroup。省略/未指定なら group 直下（未分類）。
    user_tag_subgroup_id?: string | null;
  }
) =>
  fetch(`/api/user-tag-groups/${groupUuid}/items`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagGroupItem>);

// PATCH は名称と subgroup 割当のみ更新可。is_active はサーバが受け付けない（無効化は DELETE を使う）。
// user_tag_subgroup_id: 省略=変更なし / null=未分類にクリア / uuid=その subgroup へ割当。
export const updateUserTagGroupItem = (
  itemUuid: string,
  data: {
    name_en?: string;
    name_ja?: string;
    name_zh_hans?: string;
    name_zh_hant?: string;
    name_ko?: string;
    user_tag_subgroup_id?: string | null;
  }
) =>
  fetch(`/api/user-tag-group-items/${itemUuid}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagGroupItem>);

// アイテムの論理削除（is_active=false）。サーバ仕様上、再有効化はできない。
export const deleteUserTagGroupItem = (itemUuid: string) =>
  fetch(`/api/user-tag-group-items/${itemUuid}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<UserTagGroupItem>);

// --- subgroup（中間層）CRUD ---
// subgroup は max_selections を持たず、名称のみ。付与対象ではなく item 整理用。

type SubgroupNamePayload = {
  name_en?: string;
  name_ja?: string;
  name_zh_hans?: string;
  name_zh_hant?: string;
  name_ko?: string;
};

export const createUserTagSubgroup = (groupUuid: string, data: SubgroupNamePayload) =>
  fetch(`/api/user-tag-groups/${groupUuid}/subgroups`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagSubgroup>);

export const updateUserTagSubgroup = (subgroupUuid: string, data: SubgroupNamePayload) =>
  fetch(`/api/user-tag-subgroups/${subgroupUuid}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<UserTagSubgroup>);

// subgroup の論理削除（is_active=false）。配下 item はカスケードされず、未分類として残る。
export const deleteUserTagSubgroup = (subgroupUuid: string) =>
  fetch(`/api/user-tag-subgroups/${subgroupUuid}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<UserTagSubgroup>);
